/**
 * Admin · Interview languages (GET /admin/interview-languages, PATCH /admin/interview-languages/
 * {code}, owner-only PATCH) — WS4, Sep 2026.
 *
 * The nine rows of `interview_languages` (migration 0078) with the two owner switches that gate
 * each one: `Enabled` (the owner turned it on) and `Consent reviewed` (a human read the translated
 * consent summary and signed off). A language reaches a recruiter's picker only when BOTH are on;
 * everything but English ships dark. English cannot be disabled — it IS today's behaviour and the
 * consent notice every candidate sees is anchored to it.
 *
 * The consent summary is rendered here read-only, straight from CONSENT_SUMMARY in
 * packages/shared/src/languages.ts, so the owner reviews the exact static text a candidate will
 * be shown — never a runtime rendering, never a model's paraphrase (docs/GROUND_RULES.md). The
 * English source sits beside each translated paragraph because the English is the promise; a
 * translation that disagrees with it is the bug.
 *
 * Pre-migration window: the API answers with its built-in list (English on, the rest off) until
 * 0078 runs; a PATCH then fails, and that failure is shown, not swallowed.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CONSENT_SUMMARY,
  DEFAULT_INTERVIEW_LANGUAGE,
  INTERVIEW_LANGUAGES,
  INTERVIEW_LANGUAGE_CODES,
  isInterviewLanguageCode,
  type InterviewLanguageCode,
} from "@talenthop/shared";
import { api, ApiError } from "../../lib/api.js";
import { useAuth } from "../../lib/AuthContext.js";
import { Info, Alert } from "../../components/icons.js";
import { Switch, apiErrorText } from "./adminUi.js";

/** One row as the API lists it. Only `code` and the two flags are load-bearing; the voice matrix
 *  is optional so an older/pre-migration payload (or one without the columns) still renders — the
 *  static matrix in shared fills the gap for display, never for the switches. */
interface AdminInterviewLanguage {
  code: string;
  label?: string;
  nativeLabel?: string;
  ttsVoice?: string;
  sttLocale?: string;
  enabled: boolean;
  consentReviewed: boolean;
  updatedAt?: string | null;
  updatedBy?: string | null;
}
interface AdminInterviewLanguagesResponse {
  languages: AdminInterviewLanguage[];
}

type Flag = "enabled" | "consentReviewed";

export function InterviewLanguages(): ReactNode {
  const { me } = useAuth();
  const isOwner = me?.role === "owner";
  const queryClient = useQueryClient();
  const [openCode, setOpenCode] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-interview-languages"],
    queryFn: () => api.get<AdminInterviewLanguagesResponse>("/admin/interview-languages"),
  });
  const isForbidden = error instanceof ApiError && error.status === 403;

  // One PATCH per switch, applied immediately — each flip is a separate audited decision
  // (`interview_language_configured`), so there is no batch "Save" to leave half-applied.
  const patch = useMutation({
    mutationFn: ({ code, flag, value }: { code: string; flag: Flag; value: boolean }) =>
      api.patch<unknown>(`/admin/interview-languages/${encodeURIComponent(code)}`, { [flag]: value }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["admin-interview-languages"] }),
  });

  // Rows in canonical order (English first), with the static matrix filling any display field the
  // API left out. A code the API lists that this build does not know is shown as-is, unexpandable.
  const byCode = new Map((data?.languages ?? []).map((l) => [l.code, l]));
  const rows: AdminInterviewLanguage[] = [
    ...INTERVIEW_LANGUAGE_CODES.map((code) => byCode.get(code)).filter((r): r is AdminInterviewLanguage => !!r),
    ...(data?.languages ?? []).filter((l) => !isInterviewLanguageCode(l.code)),
  ];
  const offeredCount = rows.filter((r) => r.enabled && r.consentReviewed).length;

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Interview languages</h1>
          <div className="sub">
            Which languages a recruiter may offer a candidate · a language ships only when it is enabled AND
            its consent text is human-reviewed · owner-only to change · audited
          </div>
        </div>
        <span className="pill n">Owner</span>
      </div>

      {!isOwner && (
        <div className="banner info" style={{ marginBottom: 16 }}>
          <Info />
          <div>
            You can review the consent text and see the switches, but <b>only the owner can change them</b>.
          </div>
        </div>
      )}

      {isLoading && <div className="card pad mut sm">Loading interview languages…</div>}

      {isError && isForbidden && (
        <div className="banner info">
          <Info />
          <span>Your admin role doesn&apos;t have access to interview-language configuration.</span>
        </div>
      )}
      {isError && !isForbidden && (
        <div className="banner crit">
          <span>Couldn't load the interview languages. Please retry in a moment.</span>
        </div>
      )}

      {data && (
        <>
          <div className="banner warn" style={{ marginBottom: 16 }}>
            <Alert />
            <div>
              <b>Consent text is static and human-reviewed — never generated.</b> Every non-English summary
              below is an engineering draft. Mark it <i>Consent reviewed</i> only after a native speaker has read
              it here against the English source; until then the language stays out of every recruiter picker
              even if it is enabled.
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-h">
              <h3>Languages</h3>
              <span className="tag">
                {offeredCount} of {rows.length} offered to recruiters · Owner-only · audited
              </span>
            </div>
            <div className="card-b" style={{ padding: 0 }}>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Language</th>
                      <th>Voice · locale</th>
                      <th>Enabled</th>
                      <th>Consent reviewed</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const known = isInterviewLanguageCode(r.code) ? INTERVIEW_LANGUAGES[r.code] : null;
                      const isEnglish = r.code === DEFAULT_INTERVIEW_LANGUAGE;
                      const offered = r.enabled && r.consentReviewed;
                      const busy = patch.isPending && patch.variables?.code === r.code;
                      const open = openCode === r.code;
                      return (
                        <RowGroup key={r.code} open={open && !!known} code={known ? (r.code as InterviewLanguageCode) : null}>
                          <tr>
                            <td>
                              <div style={{ fontWeight: 600 }}>
                                {r.label ?? known?.label ?? r.code}
                                {(r.nativeLabel ?? known?.nativeLabel) &&
                                  (r.nativeLabel ?? known?.nativeLabel) !== (r.label ?? known?.label) && (
                                    <span className="mut" style={{ marginLeft: 8, fontWeight: 500 }}>
                                      {r.nativeLabel ?? known?.nativeLabel}
                                    </span>
                                  )}
                              </div>
                              <div className="mut sm tnum">{r.code}</div>
                            </td>
                            <td className="mut sm tnum" style={{ whiteSpace: "nowrap" }}>
                              {r.ttsVoice ?? known?.ttsVoice ?? "—"}
                              {" · "}
                              {r.sttLocale ?? known?.sttLocale ?? "—"}
                            </td>
                            <td>
                              <Switch
                                on={r.enabled}
                                disabled={!isOwner || busy || isEnglish}
                                onToggle={() => patch.mutate({ code: r.code, flag: "enabled", value: !r.enabled })}
                              />
                              {isEnglish && (
                                <div className="mut sm" style={{ marginTop: 4 }}>
                                  always on
                                </div>
                              )}
                            </td>
                            <td>
                              <Switch
                                on={r.consentReviewed}
                                disabled={!isOwner || busy || isEnglish}
                                onToggle={() =>
                                  patch.mutate({ code: r.code, flag: "consentReviewed", value: !r.consentReviewed })
                                }
                              />
                            </td>
                            <td>
                              <span className={offered ? "pill v" : "pill n"}>
                                {offered ? "Offered" : r.enabled ? "Awaiting review" : "Dark"}
                              </span>
                              {r.updatedAt && (
                                <div className="mut sm" style={{ marginTop: 4, whiteSpace: "nowrap" }}>
                                  {new Date(r.updatedAt).toLocaleString()}
                                </div>
                              )}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              {known && (
                                <button
                                  type="button"
                                  className="btn sm ghost"
                                  onClick={() => setOpenCode(open ? null : r.code)}
                                  aria-expanded={open}
                                >
                                  {open ? "Hide consent text" : "Review consent text"}
                                </button>
                              )}
                            </td>
                          </tr>
                        </RowGroup>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {patch.isError && (
                <div className="banner crit" style={{ margin: 14 }}>
                  <span>
                    {apiErrorText(
                      patch.error,
                      "Couldn't save that switch. If the interview_languages migration has not run yet, this is expected until it does.",
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

/**
 * A language's row plus, when open, its consent summary directly beneath — the translated
 * paragraphs each under their English source, in the SAME order, so a reviewer checks meaning
 * sentence by sentence. `{retentionDays}` is left as the literal token: the number is the org's
 * retention window, filled in at consent time (ConsentStep explains why it is never hard-coded),
 * and inventing one here would show the reviewer a promise no candidate is actually made.
 */
function RowGroup({
  code,
  open,
  children,
}: {
  code: InterviewLanguageCode | null;
  open: boolean;
  children: ReactNode;
}): ReactNode {
  if (!open || !code) return children;
  const summary = CONSENT_SUMMARY[code];
  const source = CONSENT_SUMMARY[DEFAULT_INTERVIEW_LANGUAGE];
  const isSource = code === DEFAULT_INTERVIEW_LANGUAGE;
  return (
    <>
      {children}
      <tr>
        <td colSpan={6} style={{ background: "var(--surface-2)" }}>
          <div style={{ padding: "6px 4px 10px", maxWidth: 900 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontWeight: 600 }}>
                Consent summary · {INTERVIEW_LANGUAGES[code].label}
              </span>
              <span className="pill n">{summary.version}</span>
              {!isSource && (
                <span className="pill r" style={{ fontSize: 12 }}>
                  engineering draft
                </span>
              )}
            </div>
            {isSource ? (
              <p className="mut sm" style={{ margin: "0 0 8px" }}>
                This is the source text. Every other language is a rendering of these {source.paragraphs.length}{" "}
                sentences, in this order — when a translation and this disagree, this is what we promised.
              </p>
            ) : (
              <p className="mut sm" style={{ margin: "0 0 8px" }}>
                Each translated sentence is shown under the English sentence it renders. Read it as a candidate
                would, then flip <i>Consent reviewed</i> above only if every sentence keeps the English meaning.
              </p>
            )}
            <ol style={{ margin: 0, paddingLeft: 20, display: "flex", flexDirection: "column", gap: 10 }}>
              {summary.paragraphs.map((p, i) => (
                <li key={i} style={{ lineHeight: 1.55 }}>
                  {!isSource && (
                    <div className="mut sm" style={{ marginBottom: 2 }}>
                      {source.paragraphs[i] ?? "(no English source for this sentence — the translation has an extra sentence)"}
                    </div>
                  )}
                  <div lang={INTERVIEW_LANGUAGES[code].webSpeechLang} style={{ fontSize: 14 }}>
                    {p}
                  </div>
                </li>
              ))}
            </ol>
            {!isSource && summary.paragraphs.length !== source.paragraphs.length && (
              <div className="banner crit" style={{ margin: "10px 0 0", fontSize: 12.5 }}>
                <span>
                  This translation has {summary.paragraphs.length} sentences; the English source has{" "}
                  {source.paragraphs.length}. Do not mark it reviewed until they match.
                </span>
              </div>
            )}
            <p className="mut sm" style={{ margin: "10px 0 0" }}>
              <code>{"{retentionDays}"}</code> is filled in from the organisation's recording-retention window when the
              candidate sees the notice.
            </p>
          </div>
        </td>
      </tr>
    </>
  );
}
